import React, { useEffect, useState } from "react";
import "swiper/css/bundle";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { useNavigate } from "react-router-dom";
import { CalendarFeatured } from "../../../../themes/Default/components/CalendarFeatured";
import { SideMenu } from "../../../../themes/Default/components/CalendarFeatured/components/SideMenu";
import { Calendar } from "../../../../themes/Default/components/Calendar";
import { Month } from "../../../../themes/Default/components/Calendar/components/Month";
import { Day, DayOffset } from "../../../../themes/Default/components/Calendar/components/Month/components/Day";
import { addDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, startOfMonth, startOfWeek } from "date-fns";
import WeeklyEvents from "../../../../themes/Default/components/CalendarFeatured/components/WeeklyEvents/WeeklyEvents";
import { Header } from "../../../../themes/Default/components/CalendarFeatured/components/WeeklyEvents/components/Header";
import { Rows } from "../../../../themes/Default/components/CalendarFeatured/components/WeeklyEvents/components/Rows";
import { Row } from "../../../../themes/Default/components/CalendarFeatured/components/WeeklyEvents/components/Rows/components/Row";
import { Cell } from "../../../../themes/Default/components/CalendarFeatured/components/WeeklyEvents/components/Rows/components/Row/components/Cell";
import { calendarManagement } from "../../../../hooks/api/CalendarManagement";
import { useTranslation } from "react-i18next";

const WeekView = () => {
    const { t } = useTranslation("common");
    const hours = Array.from({ length: 16 }, (_, i) => i + 7); 
    const [eventsByWeek, setEventsByWeek] = useState([]);
    const [calendarMonth, setCalendarMonth] = useState(new Date()); 
    const [selectedDate, setSelectedDate] = useState(new Date()); 
    const navigate = useNavigate();
    const days = eachDayOfInterval({
        start: startOfMonth(calendarMonth),
        end: endOfMonth(calendarMonth),
    });
    const { useCalendarListMutation } = calendarManagement;
    const [calendarList] = useCalendarListMutation();

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const getWeekDays = (date) => {
        const start = startOfWeek(date, { weekStartsOn: 1 });
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    };
    
    const weekDays = getWeekDays(selectedDate);

    const tabs = [
        { title: t("dayView"),  onClick: () => gotoDayView()},
        { title: t("weekView"), isActive: true},
    ];

    const gotoDayView = () => {
        navigate("/calendar-featured/day-view");
    };

    const gotoAddEvent = () => {
        navigate("/calendar/add-event");
    };

    const goToPreviousMonth = () => {
        setCalendarMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() - 1);
            return newDate;
        });
    };
    
    const goToNextMonth = () => {
        setCalendarMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + 1);
            return newDate;
        });
    };

    useEffect(() => {
        const fetchWeeklyEvents = async () => {
            const dateFrom = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
            const dateTo = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
            const response = await calendarList({
                dateFrom,
                dateTo,
            }).unwrap();

            setEventsByWeek(response.records || []);
            console.log(response.records);
        };
    
        fetchWeeklyEvents();
    }, [selectedDate]);

    useEffect(() => {
        const timeToMinutes = (time) => {
            const [hour, minutes] = time.split(":").map(Number);
            return hour * 60 + minutes;
        };
    
        const calc15MinBlocks = (start, end) => {
            return Math.ceil((timeToMinutes(end) - timeToMinutes(start)) / 15);
        };
    
        const wraps = document.querySelectorAll(".calendar-event-wrap[data-start][data-end]");
        wraps.forEach((wrap) => {
            const { start, end } = wrap.dataset;
            const blocks = calc15MinBlocks(start, end);
            wrap.style.height = `calc(var(--row-height) * ${blocks})`;
        });
    }, [eventsByWeek]);
    
    useEffect(() => {
        const wraps = document.querySelectorAll(".calendar-event-wrap");
    
        const handleWrapClick = (clickedWrap) => (e) => {
            wraps.forEach((w) => {
                if (w !== clickedWrap) w.classList.remove("is-active");
            });
            clickedWrap.classList.toggle("is-active");
            e.stopPropagation();
        };
    
        wraps.forEach((wrap) => {
            wrap.addEventListener("click", handleWrapClick(wrap));
        });
    
        const handleDocumentClick = () => {
            wraps.forEach((w) => w.classList.remove("is-active"));
        };
    
        document.addEventListener("click", handleDocumentClick);
    
        return () => {
            wraps.forEach((wrap) => {
                wrap.removeEventListener("click", handleWrapClick(wrap));
            });
            document.removeEventListener("click", handleDocumentClick);
        };
    }, [eventsByWeek]);

    return (
        <DefaultLayout>
            <TopNavigation>
                <Button title={t("calendar.addEvent")} icon="fa-regular fa-plus" onClick={gotoAddEvent}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
            <CalendarFeatured>
                    <SideMenu>
                        <Calendar 
                            hasArrow={true}
                            onPrev={goToPreviousMonth} 
                            onNext={goToNextMonth}
                        >
                            <Month
                                month={monthNames[calendarMonth.getMonth()]}
                                year={calendarMonth.getFullYear()}
                                isWeek={true}
                            >
                                <DayOffset days={days} />
                                {days.map((day, index) => {
                                    const isWeekend = day.getDay() === 6 || day.getDay() === 0;
                                    const isSelected = isSameDay(day, selectedDate);
                                    const isToday = isSameDay(day, new Date());

                                    return (
                                        <Day
                                            key={index}
                                            isWeekend={isWeekend}
                                            isSelected={isSelected}
                                            isToday={isToday}
                                            value={format(day, "d")}
                                            onClick={() => setSelectedDate(day)}
                                        />
                                    );
                                })}
                            </Month>
                        </Calendar>
                    </SideMenu>
                    <WeeklyEvents>
                        <Header weekDays={weekDays} />
                        
                        <Rows>
                            {hours.map((hour) => (
                                <Row hour={hour}>
                                    {weekDays.map((day, dayIndex) => {
                                        const dateKey = format(day, "yyyy-MM-dd");
                                        const eventsForDay = eventsByWeek[dateKey] || [];

                                        const eventsThisHour = eventsForDay.filter((event) => {
                                            const eventHour = parseInt(event.timeFrom.split(":")[0], 10);
                                            return eventHour === hour;
                                        });

                                        return (
                                            <Cell key={dayIndex} date={dateKey} hour={hour} events={eventsThisHour} />
                                        );
                                    })}
                                </Row>
                            ))}
                        </Rows>    
                    </WeeklyEvents>
                </CalendarFeatured>
            </Main>
        </DefaultLayout>
    );
};

export default WeekView;
